import { ExpertiseStatus } from '../types/expertise-detail.types';

// Get completion percentage based on status
export const getCompletionPercentage = (status: ExpertiseStatus | string): number => {
  const statusPercentages: Record<string, number> = {
    'En attente': 0,
    'Visite programmée': 15,
    'Visite effectuée': 35,
    'Rapport en cours': 50,
    'Rapport intermédiaire': 70,
    'Rapport final': 90,
    'Terminé': 100,
    'Annulé': 0,
  };

  return statusPercentages[status] || 0;
};

// Get truncated status for display
export const getTruncatedStatus = (status: string): string => {
  const truncatedStatuses: Record<string, string> = {
    'En attente': 'En attente',
    'Visite programmée': 'Visite prog.',
    'Visite effectuée': 'Visite eff.',
    'Rapport en cours': 'Rapport',
    'Rapport intermédiaire': 'Rapport int.',
    'Rapport final': 'Rapport fin.',
    'Terminé': 'Terminé',
    'Annulé': 'Annulé',
  };

  return truncatedStatuses[status] || status;
};

// Get status color for badges and UI elements
export const getStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    'En attente': '#6B7280',      // Gray
    'Visite programmée': '#3B82F6', // Blue
    'Visite effectuée': '#F59E0B',  // Amber
    'Rapport en cours': '#8B5CF6',  // Purple
    'Rapport intermédiaire': '#10B981', // Green
    'Rapport final': '#059669',     // Dark Green
    'Terminé': '#10B981',          // Green
    'Annulé': '#EF4444',           // Red
  };

  return statusColors[status] || '#6B7280';
};

// Get status background color for badges
export const getStatusBackgroundColor = (status: string): string => {
  const statusBgColors: Record<string, string> = {
    'En attente': 'rgba(107, 114, 128, 0.1)',
    'Visite programmée': 'rgba(59, 130, 246, 0.1)',
    'Visite effectuée': 'rgba(245, 158, 11, 0.1)',
    'Rapport en cours': 'rgba(139, 92, 246, 0.1)',
    'Rapport intermédiaire': 'rgba(16, 185, 129, 0.1)',
    'Rapport final': 'rgba(5, 150, 105, 0.1)',
    'Terminé': 'rgba(16, 185, 129, 0.1)',
    'Annulé': 'rgba(239, 68, 68, 0.1)',
  };

  return statusBgColors[status] || 'rgba(107, 114, 128, 0.1)';
};

// Get timeline steps based on completion percentage
export const getTimelineSteps = (completionPercentage: number) => {
  const steps = [
    { id: 1, title: 'Demande', percentage: 0 },
    { id: 2, title: 'Analyse', percentage: 12.5 },
    { id: 3, title: 'Planification', percentage: 25 },
    { id: 4, title: 'Visite', percentage: 37.5 },
    { id: 5, title: 'Rédaction', percentage: 50 },
    { id: 6, title: 'Validation', percentage: 62.5 },
    { id: 7, title: 'Rapport', percentage: 75 },
    { id: 8, title: 'Finalisation', percentage: 87.5 },
  ];

  return steps.map(step => ({
    ...step,
    status: 
      completionPercentage >= step.percentage + 12.5 ? 'completed' :
      completionPercentage >= step.percentage ? 'current' :
      'pending'
  }));
};

// Check if intermediate report is available
export const isIntermediateReportAvailable = (completionPercentage: number): boolean => {
  return completionPercentage >= 70;
};

// Check if final report is available
export const isFinalReportAvailable = (completionPercentage: number): boolean => {
  return completionPercentage >= 100;
};

// Get urgency level based on dates
export const getUrgencyLevel = (scheduledDate?: string): 'high' | 'medium' | 'low' | null => {
  if (!scheduledDate) return null;

  const now = new Date();
  const scheduled = new Date(scheduledDate);
  const diffInDays = Math.floor((scheduled.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays < 0) return 'high';  // Overdue
  if (diffInDays <= 3) return 'high';
  if (diffInDays <= 7) return 'medium';
  return 'low';
};

// Format status for display with icon
export const getStatusDisplay = (status: string): { text: string; icon: string; color: string } => {
  const statusDisplays: Record<string, { text: string; icon: string; color: string }> = {
    'En attente': { text: 'En attente', icon: '⏳', color: '#6B7280' },
    'Visite programmée': { text: 'Visite programmée', icon: '📅', color: '#3B82F6' },
    'Visite effectuée': { text: 'Visite effectuée', icon: '✅', color: '#F59E0B' },
    'Rapport en cours': { text: 'Rapport en cours', icon: '✍️', color: '#8B5CF6' },
    'Rapport intermédiaire': { text: 'Rapport intermédiaire', icon: '📄', color: '#10B981' },
    'Rapport final': { text: 'Rapport final', icon: '📋', color: '#059669' },
    'Terminé': { text: 'Terminé', icon: '✔️', color: '#10B981' },
    'Annulé': { text: 'Annulé', icon: '❌', color: '#EF4444' },
  };

  return statusDisplays[status] || { text: status, icon: '📌', color: '#6B7280' };
};